import { ptToMm } from './units.js';
import type { TextStyle } from './schema.js';

/**
 * Text is measured once, in the shared engine, from the actual font files
 * (via fontkit). Glyph advances are the font's unkerned advance widths — the
 * same values pdf-lib writes into the PDF — so preview and export line breaks
 * and positions are identical.
 */
export interface FontkitLikeGlyph {
  id: number;
  advanceWidth: number;
  path: { toSVG(): string };
}
export interface FontkitLikeFont {
  unitsPerEm: number;
  ascent: number;
  descent: number;
  capHeight: number;
  familyName: string;
  layout(text: string): { glyphs: FontkitLikeGlyph[] };
  getGlyph(id: number): FontkitLikeGlyph;
}

export interface FontMeta {
  id: string;
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  sha256: string;
  source: 'curated' | 'uploaded';
}

export interface LoadedFont {
  meta: FontMeta;
  font: FontkitLikeFont;
}

export interface FontProvider {
  get(fontId: string): LoadedFont | undefined;
}

export class MapFontProvider implements FontProvider {
  readonly fonts = new Map<string, LoadedFont>();
  add(f: LoadedFont) {
    this.fonts.set(f.meta.id, f);
  }
  get(id: string) {
    return this.fonts.get(id);
  }
}

export interface PositionedGlyph {
  gid: number;
  /** x offset in mm from the run start */
  x: number;
}
export interface TextRun {
  text: string;
  /** left x of the run, mm */
  x: number;
  /** baseline y, mm (top-left page origin, y down) */
  y: number;
  width: number;
  glyphs: PositionedGlyph[];
}

const layoutCache = new WeakMap<FontkitLikeFont, Map<string, { gids: number[]; adv: number[] }>>();
function shape(font: FontkitLikeFont, text: string) {
  let m = layoutCache.get(font);
  if (!m) layoutCache.set(font, (m = new Map()));
  let r = m.get(text);
  if (!r) {
    const { glyphs } = font.layout(text);
    r = { gids: glyphs.map((g) => g.id), adv: glyphs.map((g) => g.advanceWidth) };
    if (m.size > 5000) m.clear();
    m.set(text, r);
  }
  return r;
}

/** Width in mm of a single line. Letter spacing is applied between glyphs (not after the last). */
export function measure(font: FontkitLikeFont, text: string, sizePt: number, letterSpacingPt = 0): number {
  if (!text) return 0;
  const { adv } = shape(font, text);
  const scale = sizePt / font.unitsPerEm;
  let w = 0;
  for (const a of adv) w += a * scale;
  return ptToMm(w + letterSpacingPt * Math.max(0, adv.length - 1));
}

export function shapeRun(font: FontkitLikeFont, text: string, sizePt: number, letterSpacingPt: number): { glyphs: PositionedGlyph[]; width: number } {
  const { gids, adv } = shape(font, text);
  const scale = sizePt / font.unitsPerEm;
  const glyphs: PositionedGlyph[] = [];
  let x = 0;
  for (let i = 0; i < gids.length; i++) {
    glyphs.push({ gid: gids[i], x: ptToMm(x) });
    x += adv[i] * scale + letterSpacingPt;
  }
  return { glyphs, width: gids.length ? ptToMm(x - letterSpacingPt) : 0 };
}

export const ascentMm = (font: FontkitLikeFont, sizePt: number) => ptToMm((font.ascent / font.unitsPerEm) * sizePt);
export const descentMm = (font: FontkitLikeFont, sizePt: number) => ptToMm((-font.descent / font.unitsPerEm) * sizePt);
export const capHeightMm = (font: FontkitLikeFont, sizePt: number) =>
  ptToMm(((font.capHeight || font.ascent * 0.7) / font.unitsPerEm) * sizePt);

/** Greedy word wrap. Words longer than the width are broken by character. */
export function wrapLines(font: FontkitLikeFont, text: string, style: TextStyle, maxWidthMm: number): string[] {
  const lines: string[] = [];
  const m = (s: string) => measure(font, s, style.sizePt, style.letterSpacingPt);
  for (const para of text.split('\n')) {
    const words = para.split(/ +/).filter((w, i, a) => w !== '' || a.length === 1);
    if (words.length === 0) { lines.push(''); continue; }
    let cur = '';
    for (const word of words) {
      const cand = cur ? cur + ' ' + word : word;
      if (m(cand) <= maxWidthMm + 1e-6) { cur = cand; continue; }
      if (cur) lines.push(cur);
      if (m(word) <= maxWidthMm + 1e-6) { cur = word; continue; }
      // break long word
      let piece = '';
      for (const ch of Array.from(word)) {
        if (m(piece + ch) > maxWidthMm && piece) { lines.push(piece); piece = ch; } else piece += ch;
      }
      cur = piece;
    }
    lines.push(cur);
  }
  return lines;
}

export interface TextBlock {
  runs: TextRun[];
  /** total height of all lines, mm */
  height: number;
  overflow: boolean;
  lineHeightMm: number;
}

/**
 * Lay out text inside a box. Baselines are placed so that each line box is
 * `sizePt × lineHeight` tall, with the font's ascent/descent centred in it.
 */
export function layoutTextBlock(
  font: FontkitLikeFont,
  rawText: string,
  style: TextStyle,
  box: { x: number; y: number; w: number; h: number },
  valign: 'top' | 'middle' | 'bottom',
  opts: { wrap?: boolean; maxLines?: number } = {},
): TextBlock {
  const text = style.uppercase ? rawText.toUpperCase() : rawText;
  let lines = opts.wrap === false ? text.split('\n') : wrapLines(font, text, style, box.w);
  const lineH = ptToMm(style.sizePt * style.lineHeight);
  const asc = ascentMm(font, style.sizePt);
  const desc = descentMm(font, style.sizePt);
  let overflow = false;
  const fitLines = Math.max(0, Math.floor((box.h + 1e-6) / lineH));
  const maxLines = opts.maxLines !== undefined ? Math.min(opts.maxLines, fitLines) : undefined;
  if (maxLines !== undefined && lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    overflow = true;
  }
  const height = lines.length * lineH;
  if (height > box.h + 0.01) overflow = true;
  let top = box.y;
  if (valign === 'middle') top = box.y + (box.h - height) / 2;
  else if (valign === 'bottom') top = box.y + box.h - height;
  const runs: TextRun[] = [];
  lines.forEach((line, i) => {
    const { glyphs, width } = shapeRun(font, line, style.sizePt, style.letterSpacingPt);
    if (width > box.w + 0.05) overflow = true;
    let x = box.x;
    if (style.align === 'center') x = box.x + (box.w - width) / 2;
    else if (style.align === 'right') x = box.x + box.w - width;
    const baseline = top + i * lineH + (lineH - (asc + desc)) / 2 + asc;
    runs.push({ text: line, x, y: baseline, width, glyphs });
  });
  return { runs, height, overflow, lineHeightMm: lineH };
}
